/* eslint-disable react/jsx-props-no-spreading */
import { useMemo } from 'react';
import { JobCardBaseProps, NavigateHandler } from '../../index';
import {
  CardLayout as Card,
  Header,
  Body,
  Footer,
  getHeaderProps,
  getFooterProps,
  type GetPropsParam,
} from '../Layout';
import { InfoList } from '../InfoList';
import { Media } from '../Media';
import { AppealDE } from '../Appeal';
import styles from './index.module.scss';

/**
 * JobCardDEProps インターフェース
 * JobCardBasePropsを拡張したプロパティ
 */
interface JobCardDEProps extends JobCardBaseProps {}

/**
 * JobCardDE コンポーネント
 * デザインエンハンスメントのための求人カード
 * @param {JobCardDEProps} props - コンポーネントのプロパティ
 * @returns {JSX.Element} JobCardDEのJSX要素
 */
export const JobCardDE = ({
  job,
  destinationURL,
  imagePriority,
  bookmarkUrlQuery,
  similarKeywordArea,
  ...handlers
}: JobCardDEProps) => {
  const {
    jid = '',
    corporateName,
    occupationName,
    isApplicationWelcome,
    isApplied,
    isBookmarkSaved,
    jobContentOutline,
    targetMemberOutline,
    jobState,
    stations,
    salary,
    salaryExamples,
    businessContent,
    imagePath,
    movieId,
    listExclusiveMessage,
    url = '',
  } = job;

  const {
    onNavigateTitle,
    onNavigateFooterButton,
    onApplyDirect,
    onApplyAgent,
    onNavigateImage,
    onBookmark,
    onNavigateToBookmarkList,
  } = handlers;

  /**
   * ヘッダーとフッターのプロパティを取得
   */
  const [headerProps, footerProps] = useMemo(() => {
    const arg: GetPropsParam = { job };
    return [getHeaderProps(arg), getFooterProps(arg)];
  }, [job]);

  /**
   * 求人詳細ページへのナビゲートハンドラーを生成
   * @param {NavigateHandler} handler - デフォルトのハンドラー
   * @returns {NavigateHandler} ナビゲートハンドラー
   */
  const handleNavigateToJobDetail =
    (handler?: NavigateHandler): NavigateHandler =>
    (event) => {
      event.currentTarget.href = destinationURL;
      if (handler) handler(event);
    };

  return (
    <Card
      isWelcome={isApplicationWelcome}
      isApplied={isApplied}
      isBookmarked={isBookmarkSaved}
    >
      <Header
        {...headerProps}
        onNavigateTitle={handleNavigateToJobDetail(onNavigateTitle)}
        onApplyDirect={onApplyDirect}
        onApplyAgent={onApplyAgent}
        similarKeywordArea={similarKeywordArea}
      />
      <Body>
        <div className={styles['jobCardDE-body']}>
          <InfoList
            detail={jobContentOutline}
            target={targetMemberOutline}
            location={jobState}
            station={stations}
            salary={{
              salary,
              example: salaryExamples,
            }}
            business={businessContent}
          />
          <div className={styles['jobCardDE-body__right']}>
            <Media
              cardType="DE"
              corporateName={corporateName}
              occupationName={occupationName}
              imagePath={imagePath}
              imgHref={url}
              jid={jid}
              movieId={movieId}
              onNavigate={handleNavigateToJobDetail(onNavigateImage)}
              imagePriority={imagePriority}
            />
            <div className={styles['jobCardDE-body__appeal']}>
              <AppealDE message={listExclusiveMessage} />
            </div>
          </div>
        </div>
      </Body>
      <Footer
        {...footerProps}
        bookmarkUrlQuery={bookmarkUrlQuery}
        onNavigateFooterButton={handleNavigateToJobDetail(
          onNavigateFooterButton
        )}
        onBookmark={onBookmark}
        onNavigateToBookmarkList={onNavigateToBookmarkList}
      />
    </Card>
  );
};
